import { useEffect, useState, useCallback } from 'react'
import { api, type Page } from '../api/client'
import type { Customer } from '../api/customers'
import { toDomainCustomerType } from './CustomerFormPage'
import { SortableTh } from '../components/SortableTh'
import {
  ButtonLink,
  IconButtonLink,
  Callout,
  DataTable,
  EmptyState,
  FilterBar,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Pagination,
  Select,
  StatusPill,
  TextField,
  type PillTone,
} from '../components/ui'

// TASK-0166: keyed by the English domain vocabulary (Customer.CustomerType, TASK-0165's 6-way
// mapping) — `c.customerType` here is always an API response value, never the Italian form value
// (that only exists inside CustomerFormPage.tsx's own <select>/toDomainCustomerType). Labels
// reused verbatim from CustomerFormPage.tsx's own <option> text to avoid inventing different
// Italian wording for the same concept.
const TYPE_LABELS: Record<string, string> = {
  person: 'Persona Fisica',
  sole_proprietor: 'Ditta Individuale',
  professional: 'Professionista',
  company: 'Azienda',
  condominium: 'Condominio',
  other: 'Altro',
}

// TASK-0065: Customer.Status's real vocabulary (Customer.cs line 30:
// active|inactive|suspended, plus the separate MergedStatus="merged" tombstone value) — these
// used to be prospect/lead/customer/inactive/blacklist, a sales-funnel vocabulary no path in the
// backend ever writes to Customer.Status (verified: nothing in the codebase ever assigns
// "prospect"/"lead"/"customer" to it). Only "inactive" ever coincided by accident, which is why
// every other row's badge silently rendered the raw, unmapped status string before this fix.
// Mirrors CustomerDetailPage.tsx's own `statusBadge` labels/colors exactly, for consistency.
const STATUS_COLORS: Record<string, PillTone> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'error',
  merged: 'neutral',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Attivo',
  inactive: 'Inattivo',
  suspended: 'Sospeso',
  merged: 'Unito',
}

const PAGE_SIZE = 25

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function displayName(c: Customer): string {
  // TASK-0166: c is an API response object, so c.customerType is always the English domain
  // value ('company'), never the Italian form value ('azienda') — this comparison was always
  // false before this fix, so every company customer rendered its customerCode instead of name.
  if (c.customerType === 'company') return c.companyName ?? c.customerCode
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

export function CustomersPage() {
  // Applied filter state (triggers fetch)
  const [search, setSearch] = useState('')
  const [contactType, setContactType] = useState('')
  const [status, setStatus] = useState('')
  const [province, setProvince] = useState('')
  const [page, setPage] = useState(1)
  // F-095: ordinamento server-side (created_desc | created_asc | name_* | code_*)
  const [sort, setSort] = useState('created_desc')

  // Pending form state
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingContactType, setPendingContactType] = useState('')
  const [pendingStatus, setPendingStatus] = useState('')
  const [pendingProvince, setPendingProvince] = useState('')

  const [data, setData] = useState<Page<Customer> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams()
      if (search) q.set('search', search)
      // TASK-0065: the domain (Customer.CustomerType) only knows 'person'/'company' — reuse
      // CustomerFormPage.tsx's own toDomainCustomerType rather than re-deriving a second mapping.
      if (contactType) q.set('customerType', toDomainCustomerType(contactType))
      // status is already the real domain vocabulary (active/inactive/suspended) straight from
      // the <select> below — no mapping needed, unlike customerType above.
      if (status) q.set('status', status)
      if (province) q.set('province', province)
      q.set('page', String(page))
      q.set('pageSize', String(PAGE_SIZE))
      q.set('sort', sort)
      const result = await api<Page<Customer>>(`/api/customers?${q}`)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [search, contactType, status, province, page, sort])

  useEffect(() => {
    void load()
  }, [load])

  function handleFilter() {
    setSearch(pendingSearch)
    setContactType(pendingContactType)
    setStatus(pendingStatus)
    setProvince(pendingProvince)
    setPage(1)
  }

  function handleSort(next: string) {
    setSort(next)
    setPage(1)
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <PageShell>
      <PageHeader
        title="Clienti"
        icon="person"
        count={total}
        actions={
          <ButtonLink to="/customers/create" variant="filled" icon="add">
            Nuovo
          </ButtonLink>
        }
      />

      <FilterBar onSubmit={handleFilter}>
        <TextField
          label="Cerca nome, email, telefono, CF..."
          placeholder="Cerca nome, email, telefono, CF..."
          value={pendingSearch}
          onChange={e => setPendingSearch(e.target.value)}
        />
        <Select
          label="Tipo"
          value={pendingContactType}
          onChange={e => setPendingContactType(e.target.value)}
        >
          <option value="">Tutti i tipi</option>
          <option value="persona_fisica">Persona Fisica</option>
          <option value="ditta_individuale">Ditta Individuale</option>
          <option value="professionista">Professionista</option>
          <option value="azienda">Azienda</option>
          <option value="condominio">Condominio</option>
          <option value="altro">Altro</option>
        </Select>
        <Select
          label="Stato"
          value={pendingStatus}
          onChange={e => setPendingStatus(e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          <option value="active">Attivo</option>
          <option value="inactive">Inattivo</option>
          <option value="suspended">Sospeso</option>
        </Select>
        <TextField
          label="Provincia"
          placeholder="Provincia"
          value={pendingProvince}
          onChange={e => setPendingProvince(e.target.value)}
        />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="group" title="Nessun contatto trovato" />
      ) : (
        <>
          <DataTable
            aria-label="Clienti"
            footer={total > PAGE_SIZE ? <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /> : undefined}
          >
            <thead>
              <tr>
                <SortableTh label="Nome/Azienda" field="name" sort={sort} onSort={handleSort} />
                <SortableTh label="Codice" field="code" sort={sort} onSort={handleSort} />
                <th>Tipo</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>Città</th>
                <th>Stato</th>
                <SortableTh label="Data" field="created" sort={sort} onSort={handleSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id}>
                  <td>
                    <a
                      href={`/customers/${c.id}`}
                      className="text-decoration-none md-typescale-emphasized-body-medium"
                    >
                      {displayName(c)}
                    </a>
                  </td>
                  <td>
                    <small>{c.customerCode}</small>
                  </td>
                  <td>
                    <small className="text-muted">
                      {TYPE_LABELS[c.customerType] ?? c.customerType}
                    </small>
                  </td>
                  <td>
                    <small>{c.email ?? '—'}</small>
                  </td>
                  <td>
                    <small>{c.phone ?? '—'}</small>
                  </td>
                  <td>
                    <small>{c.city ?? '—'}</small>
                  </td>
                  <td>
                    <StatusPill tone={STATUS_COLORS[c.status] ?? 'neutral'}>
                      {STATUS_LABELS[c.status] ?? c.status}
                    </StatusPill>
                  </td>
                  <td>{fmtDate(c.createdAt)}</td>
                  <td>
                    <IconButtonLink to={`/customers/${c.id}/edit`} size="sm" icon="edit" aria-label="Modifica" />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </>
      )}
    </PageShell>
  )
}
